// Ecommerce-related filters (public_user)

import { ECOMMERCE_USER_ACTIVITY_FILTER_OPTIONS } from '@supply-mover-2-0/shared';
import { createKeywordFilter, createRadioFilter } from './factories';
import type { FilterRegistry } from './types';

export const ecommerceFilters: FilterRegistry = {
	ecommerceUserSearch: {
		...createKeywordFilter({
			label: 'Search',
			// PD-17691 — the search also matches the user's accounts, by name or
			// customer number (EcommerceUsersService.getPublicUsers).
			placeholder: 'Search by name, email, phone, client or customer ID...',
			objectTable: 'public_user',
			listKeys: ['ecommerce_user'],
		}),
		queryParamName: 'ecommerceUserSearch',
	},

	ecommerceUserStatus: {
		...createRadioFilter({
			label: 'Status',
			options: [
				{ id: 'All', label: 'All' },
				{ id: 'Active', label: 'Active' },
				{ id: 'Inactive', label: 'Inactive' },
			],
			objectTable: 'public_user',
			fieldName: 'deletedDate',
			listKeys: ['ecommerce_user'],
			colorScheme: 'green',
		}),
		queryParamName: 'ecommerceUserStatus',
	},

	// PD-17191 — storefront activity. Applied on the server
	// (EcommerceUsersService.getPublicUsers) from storefront visits, the
	// Online now set and the login history, so the page and its total agree.
	ecommerceUserActivity: {
		...createRadioFilter({
			label: 'Activity',
			options: ECOMMERCE_USER_ACTIVITY_FILTER_OPTIONS.map((option) => ({
				id: option.id,
				label: option.label,
			})),
			objectTable: 'public_user',
			fieldName: 'storefrontActivity',
			listKeys: ['ecommerce_user'],
			colorScheme: 'green',
		}),
		queryParamName: 'ecommerceUserActivity',
		queryMapping: {
			objectTable: 'public_user',
			fieldName: 'storefrontActivity',
			joinType: 'custom',
		},
	},

	ecommerceUserClientSalesRep: {
		type: 'multi-select',
		label: 'Client Sales Rep',
		placeholder: 'Select sales rep...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'salesperson_id' as keyof unknown,
			primary: 'display_name' as keyof unknown,
		},
		apiEndpoint: '/users/salespeople',
		queryParamName: 'ecommerceUserClientSalesRep',
		multiple: true,
		options: [{ id: 'ME', label: 'Me' }],
		queryMapping: {
			objectTable: 'public_user',
			fieldName: 'clientSalesRepId',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['ecommerce_user'],
	},

	// Filter ecommerce users by their fk_company__id. Public users are
	// strictly company-scoped now; this filter is the multi-company tenant's
	// primary tool for keeping the global list focused. Backed by the
	// `ecommerceUserCompany` array param the service reads.
	ecommerceUserCompany: {
		type: 'multi-select',
		label: 'Company',
		placeholder: 'Select company...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
		},
		apiEndpoint: '/companies',
		queryParamName: 'ecommerceUserCompany',
		multiple: true,
		queryMapping: {
			objectTable: 'public_user',
			fieldName: 'fkCompanyId',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['ecommerce_user'],
	},
};
